import { zodResolver } from '@hookform/resolvers/zod'
import { CaretLeftIcon, CaretRightIcon, UserIcon } from '@phosphor-icons/react'
import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { Button } from '@/components/business/visitor-parking/button'
import { Card } from '@/components/business/visitor-parking/card'
import { HelpSection } from '@/components/business/visitor-parking/help-section'
import { SectionHeader } from '@/components/business/visitor-parking/section-header'
import { StepIndicator } from '@/components/business/visitor-parking/step-indicator'
import { TextInput } from '@/components/business/visitor-parking/text-input'
import { VisitorParkingPhoneInput } from '@/components/business/visitor-parking/visitor-parking-phone-input'
import { Icon } from '@/components/ui/icon'
import { useVisitorParkingStore } from '@/pages/visitor-parking/visitor-parking.store'
import { makeVisitorDataSchema, VisitorData } from '@/pages/visitor-parking/visitor-parking.types'
import { VisitorParkingFormLayout } from '@/pages/visitor-parking/visitor-parking-form-layout'

import * as S from './visitor-data-form.styled'

interface VisitorDataFormProps {
  onBack: () => void
  onNext: () => void
}

export const VisitorDataForm = ({ onBack, onNext }: VisitorDataFormProps) => {
  const { t } = useTranslation(['visitor-parking'])
  const { setVisitor, visitor } = useVisitorParkingStore()
  const schema = useMemo(() => makeVisitorDataSchema(t), [t])
  const form = useForm<VisitorData>({
    defaultValues: {
      email: visitor?.email ?? '',
      fullName: visitor?.fullName ?? '',
      licensePlate: visitor?.licensePlate ?? '',
      phone: visitor?.phone ?? '',
    },
    mode: 'onSubmit',
    resolver: zodResolver(schema),
  })

  const onSubmit = (data: VisitorData) => {
    setVisitor(data)
    onNext()
  }

  return (
    <VisitorParkingFormLayout
      footer={
        <S.Footer>
          <S.PrivacyPolicyText>
            <Trans
              components={[
                <S.PrivacyPolicyLink href="/resources/Terms_of_Service_2019.pdf" />,
                <S.PrivacyPolicyLink href="/resources/Privacy_Policy_2019.pdf" />,
              ]}
              i18nKey="visitorDataForm.termsPrompt"
              ns="visitor-parking"
            />
          </S.PrivacyPolicyText>
          <S.ButtonsContainer>
            <S.BackButton type="button" onClick={onBack}>
              <Icon icon={CaretLeftIcon} />
            </S.BackButton>
            <Button type="submit" onClick={form.handleSubmit(onSubmit)}>
              {t('visitor-parking:visitorDataForm.next')} <Icon icon={CaretRightIcon} />
            </Button>
          </S.ButtonsContainer>
        </S.Footer>
      }
    >
      <StepIndicator currentStep={1} totalSteps={3} />
      <SectionHeader
        description={t('visitor-parking:visitorDataForm.sectionDescription')}
        icon={UserIcon}
        title={t('visitor-parking:visitorDataForm.sectionTitle')}
      />
      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Card>
            <TextInput
              extraInfo={t('visitor-parking:visitorDataForm.fullName.extraInfo')}
              label={t('visitor-parking:visitorDataForm.fullName.label')}
              name="fullName"
              placeholder={t('visitor-parking:visitorDataForm.fullName.placeholder')}
            />
            <VisitorParkingPhoneInput
              extraInfo={t('visitor-parking:visitorDataForm.phone.extraInfo')}
              label={t('visitor-parking:visitorDataForm.phone.label')}
              name="phone"
            />
            <TextInput
              extraInfo={t('visitor-parking:visitorDataForm.email.extraInfo')}
              label={t('visitor-parking:visitorDataForm.email.label')}
              name="email"
              placeholder={t('visitor-parking:visitorDataForm.email.placeholder')}
            />
            <TextInput
              extraInfo={t('visitor-parking:visitorDataForm.licensePlate.extraInfo')}
              label={t('visitor-parking:visitorDataForm.licensePlate.label')}
              maxLength={10}
              name="licensePlate"
              placeholder={t('visitor-parking:visitorDataForm.licensePlate.placeholder')}
            />
          </Card>
        </form>
        <HelpSection />
      </FormProvider>
    </VisitorParkingFormLayout>
  )
}
